import React, { useCallback, useEffect, useState } from "react";
import { BookOpen, Award, Plus, Trash2, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const TIER_STYLE = {
  bronze: "bg-amber-100 text-amber-800 border-amber-300",
  silver: "bg-zinc-100 text-zinc-700 border-zinc-300",
  gold: "bg-yellow-100 text-yellow-800 border-yellow-400",
  none: "bg-zinc-50 text-zinc-400 border-zinc-200",
};

export default function PlaybookPanel() {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", level: "bronze" });
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/enterprise360/playbook");
      setD(data);
    } catch { toast.error("Could not load the playbook"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.email) { toast.error("Email required"); return; }
    try {
      await api.post("/admin/enterprise360/playbook/certification", form);
      toast.success("Certification updated");
      setForm({ name: "", email: "", level: "bronze" });
      load();
    } catch { toast.error("Could not save"); }
  };
  const remove = async (email) => {
    try { await api.delete(`/admin/enterprise360/playbook/certification/${encodeURIComponent(email)}`); load(); }
    catch { toast.error("Could not remove"); }
  };

  if (!d) return <div className="text-[13px] ek-text-muted py-6" data-testid="playbook-loading">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="playbook-panel">
      <div>
        <h2 className="text-lg font-semibold ek-text flex items-center gap-2">
          <BookOpen size={18} className="text-[var(--e360-primary,#7737F6)]" /> Delivery playbook &amp; certification
        </h2>
        <p className="text-[13px] ek-text-soft mt-1 max-w-3xl">
          The operating model that makes the engine repeatable across the team, and the consultant
          certification tiers that let us deliver senior-grade outcomes with a leaner team.
        </p>
      </div>

      {/* Operating model */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="playbook-operating-model">
        {d.operating_model.map((s) => (
          <div key={s.id} className="ek-surface border ek-line rounded-lg p-4" data-testid={`playbook-step-${s.id}`}>
            <div className="font-semibold ek-text text-[14px] mb-2">{s.title}</div>
            <ul className="space-y-1.5">
              {s.steps.map((st, i) => (
                <li key={i} className="text-[13px] ek-text-soft flex gap-2"><span className="text-[var(--e360-primary,#7737F6)]">•</span>{st}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Certification tiers */}
      <div>
        <div className="flex items-center gap-2 text-[13px] font-semibold ek-text-soft mb-3"><Award size={14} /> Certification tiers</div>
        <div className="grid md:grid-cols-3 gap-4">
          {["bronze", "silver", "gold"].map((k) => {
            const t = d.certification_levels[k];
            return (
              <div key={k} className={`border rounded-lg p-4 ${TIER_STYLE[k]}`} data-testid={`cert-tier-${k}`}>
                <div className="font-semibold text-[14px]">{t.label}</div>
                <p className="text-[12px] mt-1 opacity-90">{t.summary}</p>
                <ul className="mt-2 space-y-1">
                  {t.competencies.map((c, i) => <li key={i} className="text-[12px] opacity-90">✓ {c}</li>)}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Register */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[13px] font-semibold ek-text-soft">Consultant certification register</div>
          <button onClick={load} className="text-[12px] ek-text-muted hover:opacity-70 inline-flex items-center gap-1"><RefreshCw size={12} /> Refresh</button>
        </div>
        <div className="flex flex-wrap gap-2 mb-3 items-center" data-testid="cert-add-form">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name"
            className="ek-input border ek-line rounded px-3 py-1.5 text-[13px]" data-testid="cert-name-input" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email"
            className="ek-input border ek-line rounded px-3 py-1.5 text-[13px]" data-testid="cert-email-input" />
          <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}
            className="ek-input border ek-line rounded px-3 py-1.5 text-[13px]" data-testid="cert-level-select">
            {["bronze", "silver", "gold", "none"].map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <button onClick={save} data-testid="cert-save-btn"
            className="inline-flex items-center gap-1 bg-[var(--e360-primary,#7737F6)] text-white rounded px-3 py-1.5 text-[13px] hover:opacity-90">
            <Plus size={13} /> Set
          </button>
        </div>
        <div className="ek-surface border ek-line rounded-lg overflow-hidden">
          <table className="w-full text-[13px]" data-testid="cert-register-table">
            <thead><tr className="ek-surface-2 text-left ek-text-soft">
              <th className="p-3 font-medium">Consultant</th><th className="p-3 font-medium">Email</th>
              <th className="p-3 font-medium">Level</th><th className="p-3 font-medium"></th></tr></thead>
            <tbody>
              {d.register.length === 0 ? (
                <tr><td colSpan={4} className="p-4 ek-text-muted text-center">No consultants certified yet.</td></tr>
              ) : d.register.map((r) => (
                <tr key={r.email} className="border-t ek-line" data-testid={`cert-row-${r.email}`}>
                  <td className="p-3 ek-text">{r.name}</td>
                  <td className="p-3 ek-text-muted">{r.email}</td>
                  <td className="p-3"><span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${TIER_STYLE[r.level]}`}>{r.level}</span></td>
                  <td className="p-3 text-right"><button onClick={() => remove(r.email)} className="ek-text-muted hover:text-red-600" data-testid={`cert-delete-${r.email}`}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
